import { Anchor, Flex, Group, Stack, Text } from "@mantine/core";
import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import {
  deleteContact,
  deleteFilterContacts,
  downloadFilterListAll,
  getLists,
  getListsQuick,
  searchAllContactlist,
} from "../../../../../api/ContactAPI";
import MainTable from "../../../../../layouts/Table/MainTable";
import ItemDeleter from "../../../../../components/TableComponents/ItemDeleter";
import { useContactHandler } from "../../components/useContactHandler";
import { Link } from "react-router-dom";
import TableFilterExport from "../../../../../components/TableComponents/TableFilterExport";
import TableFilterDelete from "../../../../../components/TableComponents/TableFilterDelete";
import TableFilterCopyContacts from "../../components/copy/TableFilterCopyContacts";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import ColumnAdder from "./ColumnAdder";
import { getPermission } from "../../../../../functions/utilFunctions";
import ListNumbers from "./ListNumbers";
import { useListStore } from "../../../../../stores/ListStore";

const ListsContentAll = ({ isMinimized }) => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const [reloadColumns, setReloadColumns] = useState(true);

  const startingColumns = useMemo(
    () => [
      numberColumn(),
      {
        field: "contact_list_id",
        id: "contact_list_id",
        headerName: lang.prm.details.campaigns_table.contact_list,
        option_name: lang.prm.filter_labels.contact_list,
        type: "lazy",
        options: [],
        filterable: true,
        getter: getListsQuick,
        responseField: "contact_lists",
        storeKey: "listOptions",
        setStoreKey: "setListOptions",
        nameField: "name",
        disabledCondition: true,
        width: 150,
        renderCell: ({ row, value }) => {
          return (
            <Anchor
              component={Link}
              to={`/contact-lists/${value}`}
              target="_self"
              c="inherit"
              fw={800}
              truncate="end"
            >
              {row.contact_list_name}
            </Anchor>
          );
        },
      },
    ],
    [lang.prm.details.campaigns_table.contact_list]
  );

  const {
    loading,
    columns,
    rows,
    rowCount,
    serverInfo,
    handleLoading,
    onFilterChange,
    onPaginationChange,
    onSortChange,
  } = useContactHandler({
    apiRef,
    getFirstPage: searchAllContactlist,
    searchFunction: searchAllContactlist,
    reloadColumns,
    setReloadColumns,
    startingColumns,
    additionalFields: { bounced: null }
  });

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <ItemDeleter
          title_bold={`${row["%first_name%"]} ${row["%last_name%"]}`}
          handleDelete={deleteContact}
          deletedMessage={lang.contact_lists.delete_success}
          parentId={row.contact_list_id}
          id={row.id}
          apiRef={apiRef}
        />
      );
    },
    [apiRef, lang.contact_lists.delete_success]
  );

  const toolbarActions = useCallback(
    ({ selectedIds, filter }) => (
      <>
        <TableFilterCopyContacts
          isGlobal
          selectedIds={selectedIds}
          filter={filter}
          name={"ALl list search"}
          filterResultNumber={rowCount}
          total={serverInfo?.number_of_results}
          onFinish={() => {
            useListStore.getState().setListingLoad(true);
          }}
        />
        {getPermission("displayContactListExport") && (
          <TableFilterExport
            selectedIds={selectedIds}
            filter={filter}
            handleExport={downloadFilterListAll}
            name={"All List"}
            filterResultNumber={rowCount}
            total={rowCount}
            apiRef={apiRef}
          />
        )}
        <TableFilterDelete
          isGlobal
          selectedIds={selectedIds}
          filter={filter}
          handleDelete={deleteFilterContacts}
          apiRef={apiRef}
          onFinish={(type) => {
            handleLoading(type !== "selection");
            useListStore.getState().setListingLoad(true);
          }}
          filterResultNumber={rowCount}
        />
      </>
    ),
    [apiRef, handleLoading, rowCount, serverInfo?.number_of_results]
  );

  const getColumnActions = (handleClose) => (
    <ColumnAdder
      handleColumnCreated={() => {
        handleClose();
        setReloadColumns(true);
      }}
    />
  );

  const params = new URLSearchParams(location.search);
  const initialQuery = params.get("query");
  const initialFilter = params.get("filter");

  const lists = useListStore((s) => s.lists);
  const currentList = useMemo(() => {
    const totals = lists.reduce(
      (acc, curr) => {
        acc.number_of_contacts += curr.number_of_contacts || 0;
        acc.number_of_emails += curr.number_of_emails || 0;
        acc.number_of_linkedin_url += curr.number_of_linkedin_url || 0;
        acc.number_of_companies += curr.number_of_companies || 0;
        return acc;
      },
      {
        number_of_contacts: 0,
        number_of_emails: 0,
        number_of_linkedin_url: 0,
        number_of_companies: 0,
      }
    );

    return totals;
  }, [lists]);

  if (!columns) {
    return;
  }

  return (
    <Stack h={"100%"} flex={1} pr={16} gap={8} pt={12} miw={800}>
      <Group mt={4}>
        <Text fw={700} fz={24}>
          {lang.prospects.all_contacts}
        </Text>
      </Group>

      <Group mb={isMinimized ? 24 : 0} mt={0}>
        <ListNumbers currentList={currentList} serverInfo={currentList} />
      </Group>

      <Flex h={0} flex={1} mt={10}>
        <MainTable
          isClient={false}
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          setLoading={() => {
            handleLoading();
          }}
          id={"list-table"}
          hasFolder
          toolbarActions={toolbarActions}
          pageName={"contact_lists_contacts"}
          pageOrderName="contact_list_fetch_contacts_order"
          onPaginationChange={onPaginationChange}
          actionsCell={actionsCell}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          actionsCellWidth={50}
          allow200PerPage
          defaultPerPage={30}
          withBorderTop
          whiteButtons
          getColumnActions={getColumnActions}
          defaultSearch={initialQuery}
          defaultFilter={initialFilter ? JSON.parse(initialFilter) : undefined}
          additionalFilter={[
            {
              field_name: "bounced",
              label: "Bounce",
              option_name: "Bounce",
              name: "Bounce",
              id: "bounced",
              type: "exist",
              filterable: true,
              sortable: false,
              getOptions: () => [
                { id: "true", name: "Oui" },
                { id: "false", name: "Non" },
              ],
            },
          ]}
          tableSx={{
            overflow: "hidden",
            "& .MuiDataGrid-pinnedColumns": {
              background: "#fff",
              borderTopLeftRadius: 3,
              border: "none",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "#fff",
              border: "none",
            },
            "& .table-search": {
              width: "300px !important",
            },
            // "& .MuiDataGrid-pinnedColumns--right .table-row": {
            //   borderTopRightRadius: "12px !important",
            //   borderBottomRightRadius: "12px !important",
            // },
            // "& .MuiDataGrid-pinnedColumns--left .table-row": {
            //   borderTopLeftRadius: "12px !important",
            //   borderBottomLeftRadius: "12px !important",
            // },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default ListsContentAll;
